import React, { useEffect, useMemo } from "react";
import { Spinner } from "../../../components";
import {
  useAdminUsageComparison,
  useAdminUsageTimeseries,
} from "../../../api/hooks/admin";
import { adjustWorkflowTotal, isoFromDateInput, MetricsMode } from "../shared/constants";
import { UsageGranularity } from "../../../api/admin/types";
import KpiCards from "../shared/KpiCards";
import CsvDownloadButton from "../shared/CsvDownloadButton";
import TrendChart from "./TrendChart";
import WowBarChart from "./WowBarChart";
import AgencySmallMultiples from "./AgencySmallMultiples";
import WorkflowIterations from "./WorkflowIterations";
import AgencyEngagement, { AgencyEngagementRow } from "./AgencyEngagement";

interface AgencyTotalsProps {
  mode: MetricsMode;
  granularity: UsageGranularity;
  startA: string;
  endA: string;
  startB: string;
  endB: string;
  includeTestAccounts: boolean;
  showEpic: boolean;
  onFetchingChange?: (fetching: boolean) => void;
}

const AgencyTotals: React.FC<AgencyTotalsProps> = ({
  mode,
  granularity,
  startA,
  endA,
  startB,
  endB,
  includeTestAccounts,
  showEpic,
  onFetchingChange,
}) => {
  // Single-mode time series across whichever range was picked.
  // group_by="agency" so the response carries per-agency engagement counts
  // alongside the platform-wide buckets the TrendChart uses.
  const tsParams =
    startA && endA
      ? {
          startDate: isoFromDateInput(startA),
          endDate: isoFromDateInput(endA),
          granularity,
          groupBy: "agency" as const,
          includeTestAccounts,
        }
      : null;
  const tsQuery = useAdminUsageTimeseries(tsParams);

  // Compare-mode: also fetch period B time series + comparison (period A vs B).
  const tsBParams =
    mode === "compare" && startB && endB
      ? {
          startDate: isoFromDateInput(startB),
          endDate: isoFromDateInput(endB),
          granularity,
          includeTestAccounts,
        }
      : null;
  const tsBQuery = useAdminUsageTimeseries(tsBParams);

  const cmpParams =
    mode === "compare" && startA && endA && startB && endB
      ? {
          periodAStart: isoFromDateInput(startA),
          periodAEnd: isoFromDateInput(endA),
          periodBStart: isoFromDateInput(startB),
          periodBEnd: isoFromDateInput(endB),
          groupBy: "agency" as const,
          includeTestAccounts,
        }
      : null;
  const cmpQuery = useAdminUsageComparison(cmpParams);

  // Surface fetch state up so the toolbar can show a loading indicator.
  // isFetching covers both the initial cold load and refetches when params
  // change after Apply.
  const isFetching =
    tsQuery.isFetching ||
    (mode === "compare" && (tsBQuery.isFetching || cmpQuery.isFetching));
  useEffect(() => {
    onFetchingChange?.(isFetching);
  }, [isFetching, onFetchingChange]);

  const ts = tsQuery.data;
  const tsB = tsBQuery.data;
  const cmp = cmpQuery.data;

  // Per-agency engagement rows. Compare mode reads from the comparison endpoint
  // (period B = "now"); single mode reads from the timeseries response. The BE
  // populates these counts on agency-grouped responses only - the strip below
  // simply doesn't render when agency rollups aren't available.
  //
  // IMPORTANT: this useMemo MUST come before the early-return below. Moving
  // it after the spinner branch makes React see a different hook count
  // between the loading / loaded renders and throws "Rendered more hooks".
  const engagementRows: AgencyEngagementRow[] = useMemo(() => {
    if (mode === "compare") {
      return (cmp?.groups ?? []).map((g) => ({
        id: g.id,
        label: g.label,
        activeUsers: g.active_user_count_period_b ?? 0,
        totalUsers: g.user_count ?? 0,
      }));
    }
    return (ts?.groups ?? []).map((g) => ({
      id: g.id,
      label: g.label,
      activeUsers: g.active_user_count ?? 0,
      totalUsers: g.user_count ?? 0,
    }));
  }, [mode, ts, cmp]);

  if (tsQuery.isLoading || (mode === "compare" && (tsBQuery.isLoading || cmpQuery.isLoading))) {
    return (
      <div style={{ padding: 40, display: "flex", justifyContent: "center" }}>
        <Spinner />
      </div>
    );
  }

  const engagementSubtitle =
    mode === "compare"
      ? `Active = workflow events in period B (${cmp?.period_b.start?.slice(0, 10)} – ${cmp?.period_b.end?.slice(0, 10)})`
      : `Active = workflow events in this window`;

  const totalActivity =
    (ts?.buckets.reduce((s, b) => s + b.total, 0) ?? 0) +
    (tsB?.buckets.reduce((s, b) => s + b.total, 0) ?? 0);
  const showEmptyHint = totalActivity === 0;
  const showZeroDeltaHint =
    mode === "compare" && !!cmp && cmp.groups.length === 0 && totalActivity > 0;

  // CSV downloads cover the full on-screen window. In compare mode that's the
  // union of period A and period B so the file matches what the user sees.
  const csvStart = mode === "compare" ? (startA < startB ? startA : startB) : startA;
  const csvEnd = mode === "compare" ? (endA > endB ? endA : endB) : endA;
  const csvStartIso = csvStart ? isoFromDateInput(csvStart) : "";
  const csvEndIso = csvEnd ? isoFromDateInput(csvEnd) : "";
  const csvReady = !!csvStartIso && !!csvEndIso;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%" }}>
      <div
        style={{
          width: "100%",
          maxWidth: 1200,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <CsvDownloadButton
          mode="agency"
          startDate={csvStartIso}
          endDate={csvEndIso}
          filenamePrefix="agencies_export"
          disabled={!csvReady}
          disabledReason="Pick a date range first"
        />
      </div>
      {showEmptyHint ? (
        <div
          style={{
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: 8,
            padding: 14,
            width: "100%",
            maxWidth: 1200,
            color: "#7c2d12",
            fontSize: 14,
          }}
        >
          No activity found in the selected range.{" "}
          {includeTestAccounts
            ? "Try widening the date range."
            : <>Try widening the range, or check <b>Include test accounts</b> in the toolbar to see test-account activity.</>}
        </div>
      ) : null}

      {mode === "compare" && cmp ? (
        <KpiCards
          totalA={adjustWorkflowTotal(cmp.period_a.total, cmp.period_a.totals, showEpic)}
          totalB={adjustWorkflowTotal(cmp.period_b.total, cmp.period_b.totals, showEpic)}
          // pct_change comes from the BE based on the unadjusted totals; when
          // Epic is hidden we recompute against the adjusted A/B so the figure
          // matches what the user sees.
          pctChange={(() => {
            const a = adjustWorkflowTotal(cmp.period_a.total, cmp.period_a.totals, showEpic);
            const b = adjustWorkflowTotal(cmp.period_b.total, cmp.period_b.totals, showEpic);
            if (a === 0 && b === 0) return 0;
            if (a === 0) return null;
            return Math.round(((b - a) / a) * 10000) / 100;
          })()}
          labelA={`Total period A (${startA?.slice(0, 10)})`}
          labelB={`Total period B (${startB?.slice(0, 10)})`}
        />
      ) : null}

      {ts ? (
        <TrendChart
          buckets={ts.buckets}
          title={mode === "compare" ? "Period A — usage trend" : "Total account usage health"}
          subtitle={`${ts.start_date.slice(0, 10)} → ${ts.end_date.slice(0, 10)}, by ${granularity}`}
          showEpic={showEpic}
        />
      ) : null}

      {mode === "compare" && tsB ? (
        <TrendChart
          buckets={tsB.buckets}
          title="Period B — usage trend"
          subtitle={`${tsB.start_date.slice(0, 10)} → ${tsB.end_date.slice(0, 10)}, by ${granularity}`}
          showEpic={showEpic}
        />
      ) : null}

      {mode === "compare" && cmp && cmp.groups.length > 0 ? (
        <>
          <WowBarChart
            groups={cmp.groups}
            title="Period-over-period task change"
            subtitle={`${cmp.period_a.start?.slice(0, 10)}–${cmp.period_a.end?.slice(0, 10)} → ${cmp.period_b.start?.slice(0, 10)}–${cmp.period_b.end?.slice(0, 10)}`}
            showEpic={showEpic}
          />
          <AgencySmallMultiples
            groups={cmp.groups}
            title="Period-over-period change by category"
            subtitle={`Sorted by largest absolute change`}
            showEpic={showEpic}
          />
        </>
      ) : null}

      {engagementRows.length > 0 ? (
        <AgencyEngagement
          rows={engagementRows}
          subtitle={engagementSubtitle}
        />
      ) : null}

      <WorkflowIterations
        // Use period B in compare mode so iterations match the "now" view
        // (consistent with the headline KPI strip).
        startDate={
          mode === "compare" && startB && endB
            ? isoFromDateInput(startB)
            : startA
            ? isoFromDateInput(startA)
            : ""
        }
        endDate={
          mode === "compare" && startB && endB
            ? isoFromDateInput(endB)
            : endA
            ? isoFromDateInput(endA)
            : ""
        }
        includeTestAccounts={includeTestAccounts}
      />

      {showZeroDeltaHint ? (
        <div
          style={{
            background: "#f3f4f6",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            padding: 14,
            width: "100%",
            maxWidth: 1200,
            color: "#374151",
            fontSize: 14,
          }}
        >
          No agencies with non-zero change in the selected periods.
        </div>
      ) : null}
    </div>
  );
};

export default AgencyTotals;
